import { PIN_FAILED_MESSAGE } from './fileBytes';

/**
 * Strip the "what to do" tail off a per-photo error so the composed message
 * says it once. Error strings end with "Please try again." / "Please remove
 * it and choose it again." — the composed line supplies its own call to action.
 */
export function failureReason(error) {
  const raw = String(error?.message || error || '').trim();
  const stripped = raw.replace(/\s*(Please|Tap)\b[^.]*\.\s*$/u, '').trim();
  return stripped || "We couldn't save this photo.";
}

/**
 * One plain sentence for the customer, naming the photo by its position in
 * what they picked (the only handle that survives an unreadable file) and
 * saying what to do next. `single` is the cover step.
 */
export function describeFailedUpload({ error, position, total, single = false }) {
  const reason = failureReason(error);
  const fileIsDead = String(error?.message || '') === PIN_FAILED_MESSAGE;

  let subject;
  if (single) subject = "Your cover photo didn't upload.";
  else if (position && total > 1) subject = `Photo ${position} of ${total} didn't upload.`;
  else if (position && total === 1) subject = "Your photo didn't upload.";
  else subject = "One of your photos didn't upload.";

  let action;
  if (fileIsDead) action = single ? 'Choose it again.' : 'Remove it and choose it again.';
  else action = single ? 'Tap Retry, or choose it again.' : 'Tap Retry, or remove it and choose it again.';

  return `${subject} ${reason} ${action}`;
}

const listPositions = (positions) => {
  const sorted = [...positions].sort((a, b) => a - b);
  if (sorted.length <= 2) return sorted.join(' and ');
  return `${sorted.slice(0, -1).join(', ')} and ${sorted[sorted.length - 1]}`;
};

const pluralizeReason = (reason) =>
  reason.replace(/\bthis photo\b/gu, 'these photos').replace(/\bsave it\b/gu, 'save them');

/**
 * Messages for a set of failed photos. Failures with the same cause collapse
 * into one line listing their positions ("Photos 3, 7 and 12 of 20…") so a
 * dropped connection mid-batch doesn't produce a wall of identical
 * sentences; different causes stay on separate lines because the advice
 * differs (a dead file can't be retried).
 *
 * @param {Array<{ error: any, position?: number, total?: number }>} failures
 * @returns {string[]}
 */
export function describeFailedUploads(failures, { single = false } = {}) {
  if (!failures || failures.length === 0) return [];
  if (failures.length === 1 || single) {
    return failures.map((f) => describeFailedUpload({ ...f, single }));
  }

  const groups = new Map();
  failures.forEach((f) => {
    const reason = failureReason(f.error);
    const fileIsDead = String(f.error?.message || '') === PIN_FAILED_MESSAGE;
    const key = `${fileIsDead ? 'dead' : 'retry'}|${reason}`;
    if (!groups.has(key)) groups.set(key, { reason, fileIsDead, items: [] });
    groups.get(key).items.push(f);
  });

  return [...groups.values()].map(({ reason, fileIsDead, items }) => {
    if (items.length === 1) return describeFailedUpload({ ...items[0] });

    const positions = items.map((f) => f.position).filter((p) => Number.isFinite(p));
    const total = Math.max(...items.map((f) => f.total || 0));
    const subject =
      positions.length === items.length && total > 1
        ? `Photos ${listPositions(positions)} of ${total} didn't upload.`
        : `${items.length} of your photos didn't upload.`;
    const action = fileIsDead
      ? 'Remove them and choose them again.'
      : 'Tap Retry to try them all again, or remove them and choose them again.';
    return `${subject} ${pluralizeReason(reason)} ${action}`;
  });
}
